import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  TProprties,
  TProprtiesResponse,
} from "../../components/resurs-props/field/types";
import { downloadFile } from "../../utils/file";
import { RpcClient } from "../rpc-socket/rpcClient";

export const debounce = <F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<F>) => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
    timeout = setTimeout(() => func(...args), waitFor);
  };

  return debounced as (...args: Parameters<F>) => ReturnType<F>;
};

export namespace ResursTreeApi {
  export type treeName =
    | "device"
    | "user"
    | "home"
    | "balance"
    | "operator"
    | "planner"
    | "config";
  export type treeNode = "root" | number;

  export type TGetTreeRequest = {
    tree: treeName;
    objectId: Array<number>;
  };

  export type TGetTreePropsRequest = {
    tree: string;
    objects: Array<number | string>;
  };
  export type TTreeNode = {
    className: string;
    has_children: boolean;
    icon: string;
    id: string;
    parent: number;
    state: number;
    stateText: string;
    text: string;
    type: string;
  };
  export type TTreeNodeList = Array<TTreeNode>;

  export type TGetTreeResponse = {
    ins: TTreeNodeList;
  };

  export function getTree(tree: treeName) {
    return createAsyncThunk(
      `tree/getTree/${tree}`,
      async function (subrequest: { objectId: string[]; toggle?: () => void }) {
        const request = { tree, objectId: subrequest.objectId };
        try {
          const response: TGetTreeResponse = (await RpcClient.call(
            "getTree",
            request
          )) as TGetTreeResponse;

          if (subrequest.toggle) {
            subrequest.toggle();
          }

          return {
            tree: request.tree,
            data: response,
          };
        } catch (error) {
          throw error;
        }
      }
    );
  }

  export type TCanDropRequest = {
    treeSource: string;
    treeDest: string;
    idSource: string[];
    idDest: string;
  };

  export const canCreateTreeLink = createAsyncThunk<boolean, TCanDropRequest>(
    "tree/canCreateTreeLink",
    async (request: TCanDropRequest) => {
      return RpcClient.call("canCreateTreeLink", request) as Promise<boolean>;
    }
  );

  export const createTreeLink = (request: TCanDropRequest) => {
    return RpcClient.call("createTreeLink", request) as Promise<boolean>;
  };

  export const getPropertysRedux = createAsyncThunk(
    "tree/getPropertys",
    async (request: TGetTreePropsRequest) => {
      // Инициализируем новый запрос и сохраняем его в ongoingRequest
      const requestPromise = RpcClient.call(
        "getPropertys",
        request
      ) as Promise<TProprtiesResponse>;

      try {
        const data = await requestPromise;

        return data;
      } catch (error) {
        // В случае ошибки также сбрасываем ongoingRequest
        throw error;
      }
    }
  );
  export const getPropertys = (request: TGetTreePropsRequest) => {
    return RpcClient.call(
      "getPropertys",
      request
    ) as Promise<TProprtiesResponse>;
  };

  export type TGetHouseListRequest = {
    withLocation: boolean;
    withCounters: boolean;
    fullList: boolean;
  };

  export type TGetHouseListResponse = {
    useMarkerCluster: boolean;
    houseList: ReadonlyArray<{
      lat: number;
      lng: number;
      id: number;
      name: string;
      address: string;
      comment: string;
      counters: {
        list: ReadonlyArray<{
          name: string;
          count: number;
        }>;
        totalCount: number;
      };
    }>;
  };

  export const getHouseList = (request: TGetHouseListRequest) => {
    return RpcClient.call(
      "getHouseList",
      request
    ) as Promise<TGetHouseListResponse>;
  };

  export type DeviceTreeItems = "Channel" | "Iface" | "Device" | "RegDevice";

  export type TCreateDeviceTreeSelect = {
    level: DeviceTreeItems;
    selectedClass?: string;
    objectId: (string | number)[];
  };

  export type TreeDataItems = {
    channel: {
      name: string;
      className: string;
      count: number;
      active: boolean;
    };
    iface: {
      name: string;
      className: string;
      count: number;
      active: boolean;
    };
    device: {
      name: string;
      className: string;
      count: number;
      active: boolean;
      isRegistrator: boolean;
    };
    regDevice?: {
      name: string;
      className: string;
      count: number;
      active: boolean;
    };
  };

  export type Channel = {
    className: string;
    count: number;
    active: boolean;
  };

  export type Iface = {
    className: string;
    count: number;
    active: boolean;
  };

  export type RegDevice = {
    className: string;
    count: number;
    active: boolean;
  };
  export type Device = {
    className: string;
    count: number;
    active: boolean;
  };

  export type TCreateDeviceTree = {
    objectId: string[];
    options: {
      channel: Channel;
      iface: Iface;
      device: Device;
      regDevice?: RegDevice;
    };
  };

  export type TCreateTreeRequest = {
    options: {
      mc: number;
      home: number;
      entrance: number;
      flat: number;
      userType: string;
      userCount: number;
      userAccount: number;
    };
  };
  /**
   * @param {string} treeName  // необходимо учитывать дерево
   * @param {number} mc   // кол-во управляющих компаний
   * @param {number} home  // кол-во домов
   * @param {number} entrance  // кол-во подъездов в каждом доме
   * @param {number} flat // кол-во квартир в каждом подъезде
   * @param {number} userType // тип абонента в каждой квартире (simple - единый счёт, host - мультисчёт)
   * @param {number} userCount // кол-во абонентов в каждой квартире
   * @param {number} userAccount // кол-во счетов у каждого абонента (для мультисчёта)
   * @returns В ответ присылаем стандартный tree:upd!
   */
  export const createUserTree = (request: TCreateTreeRequest) => {
    return RpcClient.call("createUserTree", request);
  };

  export const createDeviceTreeSelect = (
    request: TCreateDeviceTreeSelect
  ): Promise<TCreateDeviceTreeSelectResponse> => {
    return RpcClient.call("createDeviceTreeSelect", request);
  };

  export const createDeviceTree = (request: TCreateDeviceTree) => {
    return RpcClient.call("createDeviceTree", request);
  };

  export const createDevcieTreeCheck = (
    request: isAvailableCheck
  ): Promise<boolean> => {
    return RpcClient.call("isAvailable", request);
  };

  export type isAvailableCheck = {
    objectId?: number[] | string[] | null;
    what:
      | "exportDeviceTree"
      | "exportUserTree"
      | "createDeviceTree"
      | "exportConsumption";
  };

  export type TCreateDeviceTreeSelectResponse = {
    list: {
      className: string;
      name: string;
      isRegistrator?: boolean; // Заполняется при ответе "level": "Device"
    }[];
  };

  export type TTreeUpdateResponse = {
    tree: treeName;
  };

  export type TTreeUpdateResponseIns = {
    ins: TTreeNodeList;
  } & TTreeUpdateResponse;

  export type TTreeUpdateResponseUpd = {
    upd: ReadonlyArray<{
      id: string;
      text: string;
      state: number;
      count_child?: number;
    }>;
  } & TTreeUpdateResponse;

  export type TTreeUpdateResponseSet = {
    set: ReadonlyArray<number>;
  } & TTreeUpdateResponse;

  export type TTreeUpdateResponseDel = {
    del: ReadonlyArray<string>;
  } & TTreeUpdateResponse;

  export type TSearchInTreeRequest = {
    tree: string;
    search: string;
    pos: number;
  };

  export type TSearchInTreeResponseFound = {
    tree: string;
    objectId: ReadonlyArray<number>;
  };

  export type TSearchInTreeResponseNotFound = false;

  export type TSearchInTreeResponse =
    | TSearchInTreeResponseFound
    | TSearchInTreeResponseNotFound;

  export const searchInTree = (request: TSearchInTreeRequest) => {
    return RpcClient.call(
      "searchInTree",
      request
    ) as Promise<TSearchInTreeResponse>;
  };

  export const exportTreeToXLSX = (tree: string) => {
    return RpcClient.call("exportTreeToXLSX", { tree }) as Promise<{
      url: string;
    }>;
  };

  export type TGetTreeObjectListAvailableToAddRequest = {
    tree: treeName;
    parentId: Array<string>;
    nodeClass: string;
  };
  export type TGetTreeObjectListAvailableToAddResponse = {
    filterListCaption?: string;
    filterList?: Array<{
      id: string;
      type: string;
    }>;
    listCaption: string;
    list: Array<{
      name: string;
      className: string;
      group?: string;
      type?: string; // Может быть любым - вернется при создании объекта
      filter?: string[];
    }>;
  };
  export const getTreeObjectListAvailableToAdd = (
    request: TGetTreeObjectListAvailableToAddRequest
  ) => {
    return RpcClient.call(
      "getTreeObjectListAvailableToAdd",
      request
    ) as Promise<TGetTreeObjectListAvailableToAddResponse>;
  };

  export type TCreateObjectRequest = {
    tree: treeName;
    className: string;
    parentId: Array<string>;
    objType: string;
    activate: boolean;
    count: number;
  };

  export type TCreateObjectResponse = {
    objectId: string;
  };

  export const createObject = (request: TCreateObjectRequest) => {
    return RpcClient.call(
      "createObject",
      request
    ) as Promise<TCreateObjectResponse>;
  };

  export type TDelObjectRequest = {
    tree: treeName; // Учитывает из какого дерева происходит удаление
    objects: Array<{
      objectId: number;
      parentId: number;
    }>;
  };

  export const delObject = (request: TDelObjectRequest) => {
    return RpcClient.call("delObject", request) as Promise<boolean>;
  };
  export type TShouldCallGetTree = {
    tree: treeName;
  };

  export type TSetPropertysRequest = {
    tree: string;
    objectId: string[];
    data: {
      [key: string]: string;
    };
  };

  export const setPropertys = (request: TSetPropertysRequest) => {
    return RpcClient.call(
      "setPropertys",
      request
    ) as Promise<TProprtiesResponse>;
  };

  export type TExecObjectCommandRequest = {
    tree: string; // Учитывает из какого дерева происходит редактирование объекта
    treeNeighbor?: string; // Текущее соседнее дерево в зависимости от текущей выбранной вкладки
    objectId: Array<string>;
    command: string; // Идентификатор команды,
    parameterList?: Array<TExecObjectCommandRequestParameterListItem>; // параметры команды
  };
  export type TExecObjectCommandRequestParameterListItem = {
    [key: string]: string;
  };
  export type TExecObjectCommandResponseParameterListItem = {
    key: string;
    type: string;
    defaultValue: string;
    templateOptions: {
      type: string;
      label: string;
      disabled: boolean;
      valid: {
        min: number;
        max: number;
      };
    };
  };
  export type TExecObjectCommandResponse = {
    key: string;
    parameterList: Array<TExecObjectCommandResponseParameterListItem>;
  };

  export type TExecObjectCommandResponseWithUrl = {
    successfull: boolean;
    url: string;
  };

  export const execObjectCommand = async (
    request: TExecObjectCommandRequest
  ) => {
    try {
      const response = (await RpcClient.call("execObjectCommand", request)) as
        | TExecObjectCommandResponse
        | TExecObjectCommandResponseWithUrl
        | boolean;

      if (typeof response === "object" && "successfull" in response) {
        if (response.successfull && response.url) {
          downloadFile(response.url);
        } else if (!response.successfull) {
          throw new Error("Последняя команда не была выполнена");
        }
      }
    } catch (error) {
      return error;
    }
  };

  export type TTreeSelect = {
    tree: treeName;
    id: Array<string>;
  };

  export type TUndoActionResponse = boolean | "updateProperties";
  export const undoAction = () => {
    return RpcClient.call("undoAction", {}) as Promise<TUndoActionResponse>;
  };

  export type TGetSelectionFilterRequest = {
    tree: treeName;
    objectId: Array<string>;
  };

  export type TSelectionFilterList = {
    groups: Array<{
      groupId: string;
      groupCaption: string;
      properties: TProprties;
    }>;
  };

  export type TGetSelectionFilterResponse = TSelectionFilterList;

  export const getSelectionFilterByProperties = (
    request: TGetSelectionFilterRequest
  ) => {
    return RpcClient.call(
      "getFilteredProperties",
      request
    ) as Promise<TGetSelectionFilterResponse>;
  };

  export type TSetSelectionFilterRequest = {
    filter: {
      [key: string]: any;
    };
    strict: boolean;
    tree: ResursTreeApi.treeName;
    treeNeighboor?: ResursTreeApi.treeName;
  };

  export const setSelectionFilterByProperties = (
    request: TSetSelectionFilterRequest
  ) => {
    return RpcClient.call(
      "setSelectionFilterByProperties",
      request
    ) as Promise<boolean>;
  };
}
